import React from 'react';

import { ActionType } from '../../../../sharedModules/getActionType';
import { LegacyActionEvent } from '../../../../types/LegacyActionEvent';
import type { ReviewData } from '../../../types/ModelInfo';
import { useHawkWidgetContext } from '../HawkWidget/HawkWidgetContext';
import StarRating from '../StarRating/StarRating';
import { TargetEnum } from '../TrackedLink/model';

import { StreamingScore } from './StreamingScore';
import styles from './styles/model-code-review.css';

interface ModelCodeReviewProps {
  review: ReviewData;
  asinLabelSuffix?: string;
  className?: string;
}

const ModelCodeReview: React.FC<ModelCodeReviewProps> = ({
  review,
  asinLabelSuffix,
  className,
}) => {
  const linkRef = React.useRef<HTMLAnchorElement | null>(null);

  const {
    translate,
    sendAnalytics,
    defaultTab,
    genericSharedComponents: { TrackedLink },
    dealData,
  } = useHawkWidgetContext();

  const getLabel = (): string => {
    switch (review.code_name) {
      case 'asin':
        if (className === 'userReviews') {
          return translate('modelCodeReviewUserReviewsDefault', ['Amazon']);
        }
        if (asinLabelSuffix) {
          return `${review.review_count} ${asinLabelSuffix}`;
        }
        if (review.review_count === 1) {
          return translate('modelCodeReviewASINLabelSingular', [String(review.review_count)]);
        }
        return translate('modelCodeReviewASINLabel', [String(review.review_count)]);
      case 'walmart':
        if (className === 'userReviews') {
          return translate('modelCodeReviewUserReviewsDefault', ['Walmart']);
        }
        if (review.review_count === 1) {
          return translate('modelCodeReviewWalmartLabelSingular', [String(review.review_count)]);
        }
        return translate('modelCodeReviewWalmartLabel', [String(review.review_count)]);
      default:
        return translate('modelCodeReviewDefaultLabel', [String(review.review_count)]);
    }
  };

  const trackLinkClick = (): void => {
    const label = getLabel();
    sendAnalytics({
      affiliate: {
        action: {
          type: ((): ActionType => {
            // Action type
            if (review) {
              switch (review.code_name) {
                case 'asin':
                  return ActionType.AMAZON_STAR_RATING;
                case 'walmart':
                  return ActionType.WALMART_STAR_RATING;
                default:
                  return review.code_name as ActionType; // TODO(Petr): HAWK-7836 remove cast
              }
            }
            return ActionType.UNKNOWN;
          })(),
          event: LegacyActionEvent.CLICK_FROM,
        },
        component: {
          label,
        },
      },
      product: {
        product: {
          primary: {
            link: review.reviews_url,
            revenueId: review.reviews_customTrackingId,
          },
        },
      },
      dealData,
    });
  };

  const containerStyle = className && styles[className] ? className : 'container';

  const streamingScores = ['imdb', 'rt'];

  if (review) {
    return (
      <div className={styles[containerStyle]}>
        {defaultTab?.category !== 'multimedia' && !streamingScores.includes(review.code_name) && (
          <>
            <TrackedLink
              {...{
                trackLinkClick,
                customTarget: TargetEnum.BLANK,
                attributes: {
                  ref: linkRef,
                  className: styles['link'],
                  href: review.reviews_url,
                },
              }}
            >
              {getLabel()}
            </TrackedLink>
            <StarRating
              {...{
                score: review && review.review_score_formatted,
                className,
                rating: review.code_name,
              }}
            />
          </>
        )}
        {(defaultTab?.category === 'multimedia' || streamingScores.includes(review.code_name)) && (
          <StreamingScore
            {...{
              review,
              key: review.code_name,
            }}
          />
        )}
      </div>
    );
  }
  return null;
};

export default ModelCodeReview;
